import { camelCase, startCase } from 'lodash-es';

import {
    ChangeOperation,
    ChangeTrackingValueFormatter,
    SafeAny,
    ValueType,
} from '@pf/shared-common';
import { IUserFragment } from '../models/fragments/core/user-fragment';
import { joinStrings } from '@pf/shared-utility';

export type ChangeFieldFormatters = Record<string, ValueType>;

export function computeChangedFields(changes: ChangeOperation[]) {
    return changes.map(change => startCase(change.path?.slice(1))).join(', ');
}

export function getChangedFieldNames(changes: ChangeOperation[]): string[] {
    return changes
        .map(change => startCase(change.path?.slice(1)))
        .filter((field): field is string => !!field);
}

export function computeChangeText(
    changes: ChangeOperation[],
    excludedFields: string[] = []
) {
    const aggregatedChanges = changes
        .filter(change => {
            const fieldPath = change.path?.slice(1) || '';
            return !excludedFields.includes(fieldPath);
        })
        .reduce(
            (pv, cv) => {
                const op = cv.op?.toLocaleLowerCase().trim();
                if (op === 'add') {
                    pv.additions.count++;
                } else if (op === 'replace') {
                    pv.updates.count++;
                } else if (op === 'remove') {
                    pv.removals.count++;
                }
                return pv;
            },
            {
                additions: { display: 'Addition(s)', count: 0 },
                updates: { display: 'Update(s)', count: 0 },
                removals: { display: 'Removal(s)', count: 0 },
            }
        );
    const changeText = Object.values(aggregatedChanges)
        .filter(x => x.count > 0)
        .map(x => `${x.count} ${x.display}`)
        .join(', ');
    return changeText;
}

export function normalizeChanges(changes?: ChangeOperation[] | null) {
    return changes?.length
        ? changes.map(c => ({
              op: c.op as string,
              path: (c.path as string)
                  .split('/')
                  .map(p => camelCase(p))
                  .join('/'),
              value: c.value,
          }))
        : [];
}

export interface ChangeDescription {
    field: string;
    description: string;
}

export function computeChangeDescriptions(
    changes: ChangeOperation[],
    entity?: SafeAny,
    valueFormatter?: ChangeTrackingValueFormatter,
    excludedFields: string[] = []
): ChangeDescription[] {
    return changes
        .filter(change => {
            const fieldPath = change.path?.slice(1) || '';
            return !excludedFields.includes(fieldPath);
        })
        .map(change => {
            const fieldPath = change.path?.slice(1) || '';
            const fieldName = startCase(fieldPath) || 'Unknown';
            const op = change.op?.toLowerCase()?.trim();

            const format = (val: SafeAny) =>
                valueFormatter
                    ? valueFormatter(fieldPath, val, entity)
                    : formatValue(val);

            const newValue = format(change.value);
            let previousValue: string | undefined;

            if (entity && change.path) {
                try {
                    const pathParts = change.path
                        .slice(1)
                        .split('/')
                        .map(p => camelCase(p));
                    let current = entity;
                    for (const part of pathParts) {
                        current = current?.[part];
                    }
                    previousValue = format(current);
                } catch {
                    previousValue = undefined;
                }
            }

            let description: string;
            if (op === 'replace') {
                description = previousValue
                    ? `${fieldName} changed from "${previousValue}" to "${newValue}"`
                    : `${fieldName} changed to "${newValue}"`;
            } else if (op === 'add') {
                description = `${fieldName} set to "${newValue}"`;
            } else if (op === 'remove') {
                description = previousValue
                    ? `${fieldName} removed (was "${previousValue}")`
                    : `${fieldName} removed`;
            } else {
                description = `${fieldName} was modified`;
            }

            return { field: fieldName, description };
        });
}

export function formatValue(value: SafeAny): string {
    if (value === null || value === undefined) {
        return 'None';
    }
    if (typeof value === 'object') {
        if (Array.isArray(value)) {
            return `[${value.length} item(s)]`;
        }
        const name =
            value.name || value.displayName || value.label || value.title;
        if (name) {
            return String(name);
        }
        return JSON.stringify(value);
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    return String(value);
}

export const userFragmentFormatter = 'UserFragment';

export function userFragmentFormatterFn(user?: IUserFragment) {
    return joinStrings(' ', user?.firstName, user?.lastName);
}
